import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ProductServiceService } from 'src/app/services/product-service.service';
import { DbService } from 'src/app/services/db-service/db.service';
import { TemplateMasterService } from 'src/app/master_modules/masters/apps/template/template-master.service';
import { ComponentImageUploadService } from 'src/app/services/component-image-upload.service';

@Component({
  selector: 'app-create-product',
  templateUrl: './create-product.component.html',
  styleUrls: ['./create-product.component.scss']
})
export class CreateProductComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<CreateProductComponent>,
    private productService: ProductServiceService,
    private templateService: TemplateMasterService,
    private imageUploadService: ComponentImageUploadService,
    private snackBar: MatSnackBar,
    private _dbService: DbService,
    public ds: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  // ---------- VARIABLES ----------
  nodeAttachements: any[] = [];
  pageLoader = false;
  submitLoader = false;
  ecomIndustry: any[] = [];
  terms: any[] = [];
  selectedTax: any;
  selectedCategoryId: any;
  categoryList: any[] = [];
  collectionList: any[] = [];
  selectedCollectionId: any[] = [];
  selectedCollectionData: any[] = [];
  allCollectionsSelected = false;
  itemVariant: ItemVariant[] = [];

  allProducts: any[] = []; // used for duplicate product check
  allCategories: any[] = []; // used for duplicate category check

  // ---------- VALIDATION FLAGS ----------
  productNameExceeded: boolean = false;
  briefExceeded: boolean = false;

  // ---------- CHARACTER LIMIT CHECKERS ----------
  checkProductNameLength(): void {
    this.productNameExceeded = this.productData.name?.length > 50;
  }

  checkBriefLength(): void {
    this.briefExceeded = this.productData.brief?.length > 200;
  }

  // ---------- PRODUCT DATA ----------
  productData: any = {
    itemId: null,
    name: '',
    subIndustryId: '',
    itemInventory: { openingStock: 0, lowStockQuantity: 0 },
    price: { value: 0, discountedPrice: 0, purchasePrice: 0, sellingPrice: 0, tax: 0 },
    brief: '',
    descriptor: { name: '' },
    itemCategorisation: { itemCategories: [], itemCollection: [] },
    itemImages: [],
    itemVariant: [],
    createdById: this._dbService.getStaffId(),
    createdByName: this._dbService.getStaffName(),
    createdTimeStamp: new Date()
  };

  // ---------- INIT ----------
  ngOnInit(): void {
    this.getEcommerceIndustry();
    this.getAllTaxes();
    this.getAllProducts(); // fetch all products for duplicate check
    this.getAllCategories(); // fetch all categories for duplicate check
    if (this.data?.edit) this.patchEditData();
  }

  // ---------- EDIT MODE ----------
  private patchEditData(): void {
    const d = this.data.data;
    Object.assign(this.productData, d);
    this.selectedCategoryId = d?.itemCategorisation?.itemCategories?.[0]?.refId || null;
    this.nodeAttachements =
      d.itemImages?.map((img: any) => ({
        url: img.imgUrl,
        attachmentType: 'IMAGE',
        isAlreadyUploaded: true
      })) || [];
    this.selectedCollectionId =
      d.itemCategorisation?.itemCollection?.map((c: any) => c.refId) || [];
    this.selectedCollectionData = d.itemCategorisation?.itemCollection || [];
    this.itemVariant = d.itemVariant || [];
    this.getCategoryCollectionBySubIndustryId(this.productData.subIndustryId);
  }

  // ---------- IMAGE HANDLING ----------
  updateContactImage(ev: any): void {
    const files: FileList = ev.target.files;
    if (!files?.length) return;
    this.pageLoader = true;
    Array.from(files).forEach((fileData, i, arr) => {
      const reader = new FileReader();
      reader.onload = (res: any) => {
        this.nodeAttachements.push({
          url: res.target.result,
          name: fileData.name,
          attachmentType: fileData.type.includes('image') ? 'IMAGE' : 'FILE',
          file: fileData
        });
        if (i === arr.length - 1) this.pageLoader = false;
      };
      reader.readAsDataURL(fileData);
    });
  }

  delete(i: number): void {
    this.nodeAttachements.splice(i, 1);
  }

  closebutton(): void {
    this.dialogRef.close();
  }

  // ---------- FETCH DATA ----------
  getEcommerceIndustry(): void {
    this.templateService.getAllEcommerceIndustries().subscribe((res: any) => {
      this.ecomIndustry = res.data.map((x: any) => ({
        name: x.name,
        industryId: x.parent.id,
        subIndustryId: x.id
      }));
    });
  }

  getCategoryCollectionBySubIndustryId(subId: any): void {
    if (!subId) return;
    this.productService.getCategoryBySubIndustryId(subId).subscribe((r: any) => (this.categoryList = r.data || []));
    this.productService.getCollectionBySubIndustryId(subId).subscribe((r: any) => {
      this.collectionList = r.data || [];
      this.syncSelectedCollections();
    });
  }

  getAllTaxes(): void {
    this.productService.getAllTaxes().subscribe({
      next: (res: any) => {
        this.terms = res.data.data.map((t: any) => ({
          viewValue: `${t.taxValueAndDetails.taxPercent}% (${t.taxType})`,
          value: { taxId: t.taxId, taxPercent: t.taxValueAndDetails.taxPercent }
        }));
        this.selectedTax = this.terms[0]?.value;
      },
      error: (err) => console.error(err)
    });
  }

  getAllProducts(): void {
    this.productService.getProductList({ pageNo: 0, pageSize: 10000 }).subscribe({
      next: (res: any) => {
        this.allProducts = res?.data?.data || [];
      },
      error: (err: any) => {
        console.error('Error fetching product list:', err);
      }
    });
  }

  getAllCategories(): void {
    this.productService.getCategoriesList({ pageNo: 0, pageSize: 10000 }).subscribe({
      next: (res: any) => {
        this.allCategories = res?.data?.data || [];
      },
      error: (err: any) => {
        console.error('Error fetching category list:', err);
      }
    });
  }

  // ---------- TAX ----------
  changeTax(): void {
    if (!this.selectedTax) return;
    const { value, discountedPrice } = this.productData.price;
    const price = discountedPrice > 0 ? discountedPrice : value;
    const tax = (price * this.selectedTax.taxPercent) / 100;
    this.productData.price.sellingPrice = Number((price + tax).toFixed(2));
  }

  // ---------- COLLECTION ----------
  onSelectAllCollection(ev: any): void {
    this.selectedCollectionId = ev.checked ? this.collectionList.map(c => c.collectionId) : [];
    this.onCollectionsChange(this.selectedCollectionId);
  }

  onCollectionsChange(ids: any[]): void {
    this.selectedCollectionData = this.collectionList
      .filter(c => ids.includes(c.collectionId))
      .map(c => ({ refId: c.collectionId, refName: c.collectionName }));
    this.allCollectionsSelected = this.selectedCollectionData.length === this.collectionList.length;
  }

  private syncSelectedCollections(): void {
    if (this.selectedCollectionId?.length)
      this.selectedCollectionData = this.collectionList
        .filter(c => this.selectedCollectionId.includes(c.collectionId))
        .map(c => ({ refId: c.collectionId, refName: c.collectionName }));
  }

  // ---------- CREATE PRODUCT ----------
  async createItem(): Promise<void> {
    this.submitLoader = true;

    // --- Empty name validation ---
    if (!this.productData.name || !this.productData.name.trim()) {
      this.snackBar.open(' Product name cannot be empty', 'Close', { duration: 2000 });
      this.submitLoader = false;
      return;
    }

    // --- Price validation ---
    if (this.productData.price.value < 0 || this.productData.price.discountedPrice < 0) {
      this.snackBar.open(' Prices cannot be negative', 'Close', { duration: 2000 });
      this.submitLoader = false;
      return;
    }

    // --- Duplicate product name validation ---
    const newName = (this.productData.name || '').trim().toLowerCase();
    const duplicateProduct = this.allProducts.some(
      (p: any) =>
        (p.name || '').trim().toLowerCase() === newName &&
        (!this.data?.edit || p.itemId !== this.data?.data?.itemId)
    );

    if (duplicateProduct) {
      this.submitLoader = false;
      this.snackBar.open(' Product with this name already exists!', 'Close', { duration: 2000 });
      return;
    }

    // --- Duplicate category name validation ---
    const selectedCategory = this.categoryList.find(cat => cat.categoryId === this.selectedCategoryId);
    if (selectedCategory) {
      const catName = selectedCategory.name?.trim().toLowerCase();
      const duplicateCategory = this.allCategories.some(
        (c: any) => (c.name || '').trim().toLowerCase() === catName && c.categoryId !== selectedCategory.categoryId
      );
      if (duplicateCategory) {
        this.submitLoader = false;
        this.snackBar.open('Category with this name already exists!', 'Close', { duration: 2000 });
        return;
      }
    }

    // --- Image upload ---
    const imgs: any[] = [];
    try {
      for (const f of this.nodeAttachements) {
        if (!f.file) imgs.push({ imgUrl: f.url });
        else {
          const r: any = await this.imageUploadService.uploadComponentsImages(f.file, 'product_images');
          imgs.push({ imgUrl: r?.Location || f.url });
        }
      }

      const payload = { ...this.productData };
      if (this.selectedCategoryId)
        payload.itemCategorisation.itemCategories = [{ refId: this.selectedCategoryId }];
      payload.itemCategorisation.itemCollection = this.selectedCollectionData;
      payload.itemImages = imgs;

      this.productService.addNewProduct(payload).subscribe({
        next: () => {
          this.submitLoader = false;
          this.snackBar.open(
            this.data?.edit ? ' Product Updated Successfully' : ' Product Created Successfully',
            'Close',
            { duration: 2000 }
          );
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.submitLoader = false;
          this.snackBar.open(err?.error?.message || ' Failed to save product', 'Close', { duration: 2000 });
        }
      });
    } catch (e) {
      this.submitLoader = false;
      console.error(e);
      this.snackBar.open(' Image upload failed', 'Close', { duration: 2000 });
    }
  }
}

// ---------- ITEM VARIANT CLASS ----------
export class ItemVariant {
  variantId = '';
  itemId = '';
  variantName = '';
  variantImages: any[] = [];
  properties: any = {};
  itemVariantPrice: any = {};
  itemInventory: any = {};
  itemStatus = 'IN_STOCK';
  isDelete = false;
}
